(() => {
  const SKIO_RULES_KEY = 'skio-discount-rules';
  const SKIO_RULES_URL = 'https://api.skio.com/storefront-http/get-rules-by-domain-or-hostname';
  const REBUY_MOUNT_ID = 'SubscriptionUpsellRebuy';
  const LOSS_TOAST_MS = 2000;

  const state = {
    cart: null,
    pdpPairs: [],
    applying: false,
    previousAtTier: null,
    lossTimer: null,
  };

  const refresh = {
    timer: null,
    pending: false,
  };
  let hydratingRebuy = 0;

  function withRebuyWrite(fn) {
    hydratingRebuy += 1;
    try {
      return fn();
    } finally {
      hydratingRebuy -= 1;
    }
  }

  function scheduleRefresh(cart) {
    if (cart) state.cart = cart;
    if (state.applying) {
      refresh.pending = true;
      return;
    }
    clearTimeout(refresh.timer);
    refresh.timer = setTimeout(() => {
      refresh.timer = null;
      if (state.applying) {
        refresh.pending = true;
        return;
      }
      refresh.pending = false;
      refreshCartUi().catch(() => {});
    }, 150);
  }

  function withJs(url, fallback) {
    const value = url || fallback;
    return value.endsWith('.js') ? value : `${value}.js`;
  }

  function cartUrl() {
    return withJs(FoxTheme?.routes?.cart_url, '/cart');
  }

  function addUrl() {
    return withJs(FoxTheme?.routes?.cart_add_url, '/cart/add');
  }

  function changeUrl() {
    return withJs(FoxTheme?.routes?.cart_change_url, '/cart/change');
  }

  function isShopifyErrorPayload(json) {
    if (!json || typeof json !== 'object') return false;
    if (typeof json.status === 'number' && json.status >= 400) return true;
    return json.status === 'bad_request' || json.status === 'unprocessable_entity';
  }

  function fetchJson(url, options) {
    const config = FoxTheme?.utils?.fetchConfig
      ? { ...FoxTheme.utils.fetchConfig('json', options?.method || 'GET'), ...options }
      : { method: options?.method || 'GET', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, ...options };
    return fetch(url, config).then(async (response) => {
      const json = await response.json();
      if (!response.ok || isShopifyErrorPayload(json)) {
        const description = typeof json.description === 'string' ? json.description : json.message;
        const error = new Error(description || 'Cart request failed');
        error.payload = json;
        throw error;
      }
      return json;
    });
  }

  function queryInDocumentAndTemplates(selector) {
    const live = document.querySelector(selector);
    if (live) return live;
    for (const template of document.querySelectorAll('template')) {
      const inner = template.content.querySelector(selector);
      if (inner) return inner;
    }
    return null;
  }

  function parseJsonNode(node) {
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  function getThemeConfig() {
    const settings = parseJsonNode(queryInDocumentAndTemplates('[data-subscription-upsell-settings]')) || {};
    const data = parseJsonNode(queryInDocumentAndTemplates('[data-subscription-upsell-data]')) || {};
    return {
      fallbackDiscount: Number(data.fallbackDiscount || settings.fallbackDiscount) || 25,
      tierQty: Number(data.tierQty || settings.tierQty) || 2,
      cartNotificationsEnabled: settings.cartNotificationsEnabled !== false && data.cartNotificationsEnabled !== false,
      products: data.products || {},
      strings: { ...(settings.strings || {}), ...(data.strings || {}) },
    };
  }

  function volumeTierQty() {
    const qty = Math.floor(Number(getThemeConfig().tierQty));
    return Number.isFinite(qty) && qty >= 2 ? qty : 2;
  }

  function subscriptionQty(cart) {
    return (cart?.items || []).filter(isSubscription).reduce((sum, item) => sum + item.quantity, 0);
  }

  function remainingToTier(cart) {
    return Math.max(0, volumeTierQty() - subscriptionQty(cart));
  }

  function quantityWord(value) {
    const words = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
    const qty = Math.max(0, Math.floor(Number(value) || 0));
    return words[qty] || String(qty);
  }

  function quantityCopyExtras(needed) {
    return {
      quantity: quantityWord(needed),
      subscription: needed === 1 ? 'subscription' : 'subscriptions',
    };
  }

  function applyCopyTokens(text, extras) {
    let out = String(text || '');
    if (extras?.product) out = out.replaceAll('[product]', extras.product);
    if (extras?.variant) out = out.replaceAll('[variant]', extras.variant);
    if (extras?.quantity != null) out = out.replaceAll('[quantity]', String(extras.quantity));
    if (extras?.subscription) out = out.replaceAll('[subscription]', extras.subscription);
    return out;
  }

  function formatCopy(template, percent, extras) {
    return applyCopyTokens(String(template || '').replaceAll('[discount]', `${percent}%`), extras);
  }

  function escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;');
  }

  function formatUpsellMoney(cents) {
    const format = window.FoxTheme?.settings?.moneyFormat;
    if (window.FoxTheme?.Currency?.formatMoney) {
      return FoxTheme.Currency.formatMoney(cents, format);
    }
    return `$${(Number(cents) / 100).toFixed(2)}`;
  }

  function formatCopyHtml(template, percent, extras) {
    return applyCopyTokens(escapeHtml(String(template || '')), {
      product: extras?.product != null ? escapeHtml(extras.product) : undefined,
      variant: extras?.variant != null ? escapeHtml(extras.variant) : undefined,
      quantity: extras?.quantity != null ? escapeHtml(String(extras.quantity)) : undefined,
      subscription: extras?.subscription != null ? escapeHtml(extras.subscription) : undefined,
    })
      .replaceAll('[discount] off', `<strong>${percent}% off</strong>`)
      .replaceAll('[discount]', `<strong>${percent}%</strong>`);
  }

  function pushEvent(name, extra) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: name,
      ...extra,
    });
  }

  function isSkioPlanName(name) {
    const lowered = String(name || '').toLowerCase();
    return !lowered.includes('hidden-') && !lowered.includes('dynamic box subscription');
  }

  function pickSellingPlan(plans, preferredName, preferredId) {
    const raw = Array.isArray(plans) ? plans : [];
    const list = raw.filter((plan) => isSkioPlanName(plan.name));
    const pool = list.length ? list : raw;
    if (!pool.length) return null;
    if (preferredId) {
      const byId = pool.find((plan) => String(plan.id) === String(preferredId));
      if (byId) return byId;
    }
    if (preferredName) {
      const byName = pool.find((plan) => plan.name === preferredName);
      if (byName) return byName;
    }
    return pool[0];
  }

  function lineProductId(item) {
    return Number(item.product_id);
  }

  function findLineByProduct(cart, productId) {
    return (cart?.items || []).find((item) => lineProductId(item) === Number(productId));
  }

  function findLineByVariant(cart, variantId) {
    return (cart?.items || []).find((item) => Number(item.variant_id || item.id) === Number(variantId));
  }

  function isSubscription(item) {
    if (!item) return false;
    if (item.selling_plan_allocation) return true;
    const planId = item.selling_plan_id ?? item.selling_plan;
    return Boolean(planId) && Number(planId) !== 0;
  }

  function mergePartnerMap() {
    const theme = getThemeConfig();
    const products = { ...(theme.products || {}) };
    state.pdpPairs.forEach((pair) => {
      products[String(pair.currentProductId)] = {
        productId: pair.currentProductId,
        productTitle: pair.currentProductTitle,
        partner: pair.partner,
      };
      if (pair.partner?.productId) {
        products[String(pair.partner.productId)] = products[String(pair.partner.productId)] || {
          productId: pair.partner.productId,
          productTitle: pair.partner.productTitle,
          partner: {
            productId: pair.currentProductId,
            productTitle: pair.currentProductTitle,
            variantId: pair.currentVariantId,
            sellingPlans: pair.currentSellingPlans || [],
          },
        };
      }
    });
    return { ...theme, products };
  }

  function findPairForCart(cart, products) {
    const items = cart?.items || [];
    for (const item of items) {
      const mapping = products[String(item.product_id)];
      if (!mapping?.partner?.productId) continue;
      const partnerId = Number(mapping.partner.productId);
      const selfLine = findLineByProduct(cart, item.product_id);
      const partnerLine = findLineByProduct(cart, partnerId);
      return {
        currentProductId: Number(item.product_id),
        partnerProductId: partnerId,
        partner: mapping.partner,
        currentLine: selfLine,
        partnerLine,
      };
    }
    return null;
  }

  function classify(cart, products) {
    const pair = findPairForCart(cart, products);
    if (!pair) {
      return { type: 'none', pair: null };
    }

    const currentSub = pair.currentLine && isSubscription(pair.currentLine);
    const partnerSub = pair.partnerLine && isSubscription(pair.partnerLine);
    const currentOtp = pair.currentLine && !isSubscription(pair.currentLine);
    const partnerOtp = pair.partnerLine && !isSubscription(pair.partnerLine);

    if (currentSub && partnerSub) return { type: 'both_subs', pair };
    if ((currentSub && partnerOtp) || (partnerSub && currentOtp)) return { type: 'sub_plus_otp', pair };
    if ((currentSub && !pair.partnerLine) || (partnerSub && !pair.currentLine)) return { type: 'one_sub', pair };
    return { type: 'other', pair };
  }

  async function fetchCart() {
    const cart = await fetchJson(cartUrl(), { method: 'GET' });
    state.cart = cart;
    return cart;
  }

  async function fetchSkioRules() {
    const cached = cachedSkioRules();
    if (cached !== null) return cached;

    const shop = window.Shopify?.shop;
    if (!shop) return [];

    const response = await fetch(`${SKIO_RULES_URL}?domain=${shop}`);
    const json = await response.json();
    const rules = (json.rules || [])
      .filter((rule) => rule.type !== 'surpriseDelight')
      .sort((a, b) => a.minQuantityToDiscount - b.minQuantityToDiscount);
    try {
      sessionStorage.setItem(SKIO_RULES_KEY, JSON.stringify(rules));
    } catch (error) {
      // ignore quota
    }
    return rules;
  }

  function ruleVariantIds(rule) {
    return Array.isArray(rule?.productVariantIds) ? rule.productVariantIds : [];
  }

  // Skio "Add all products" rules send an empty allowlist.
  function ruleAppliesToAllProducts(rule) {
    return ruleVariantIds(rule).length === 0;
  }

  function gidMatchesItem(gid, item) {
    const value = String(gid || '');
    const variantId = String(item.variant_id || item.variantId || item.id || '');
    const productId = String(item.product_id || item.productId || item.product?.id || '');
    if (!value) return false;
    if (variantId && (value === variantId || value.endsWith(`/${variantId}`) || value.includes(`ProductVariant/${variantId}`))) {
      return true;
    }
    if (productId && (value === productId || value.endsWith(`/${productId}`) || value.includes(`Product/${productId}`))) {
      return true;
    }
    return false;
  }

  function ruleMatchesItem(rule, item) {
    if (ruleAppliesToAllProducts(rule)) return true;
    return ruleVariantIds(rule).some((gid) => gidMatchesItem(gid, item));
  }

  function eligibleRuleQuantity(cart, rule) {
    return (cart?.items || []).reduce((sum, item) => {
      if (!isSubscription(item)) return sum;
      return ruleMatchesItem(rule, item) ? sum + item.quantity : sum;
    }, 0);
  }

  function isVolumeTierRule(rule) {
    return Number(rule?.minQuantityToDiscount) === volumeTierQty();
  }

  function pickVolumeTierRule(rules, cart) {
    const tierRules = (rules || []).filter(isVolumeTierRule);
    return (
      tierRules.find((rule) => !ruleAppliesToAllProducts(rule) && eligibleRuleQuantity(cart, rule) > 0) ||
      tierRules.find((rule) => !ruleAppliesToAllProducts(rule)) ||
      tierRules[0] ||
      null
    );
  }

  function ruleMeetsTier(cart, rule) {
    if (!rule) return false;
    const min = Number(rule.minQuantityToDiscount) || volumeTierQty();
    return eligibleRuleQuantity(cart, rule) >= min;
  }

  function stackedPercents(plan, volume) {
    const planValue = Number(plan) || 0;
    const volumeValue = Number(volume) || 0;
    if (volumeValue <= 0) return planValue;
    return Math.floor((1 - (1 - planValue / 100) * (1 - volumeValue / 100)) * 100);
  }

  function sellingPlanPercentFromCart(cart) {
    return sellingPlanPercent((cart?.items || []).find(isSubscription));
  }

  async function resolveDiscount(cart) {
    const theme = mergePartnerMap();
    let percent = Number(theme.fallbackDiscount) || 25;
    const subQty = subscriptionQty(cart);
    const tierQty = volumeTierQty();
    let atTier = subQty >= tierQty;

    try {
      const rules = await fetchSkioRules();
      const tierRules = (rules || []).filter(isVolumeTierRule);
      const tierRule = pickVolumeTierRule(rules, cart);
      const plan = sellingPlanPercentFromCart(cart);
      if (tierRule?.discountAmount) {
        percent = stackedPercents(plan, tierRule.discountAmount);
      }
      if (!atTier && tierRules.length) {
        atTier = tierRules.some((rule) => ruleMeetsTier(cart, rule));
      }
    } catch (error) {
      // keep qty fallback
    }

    return { percent, atTier };
  }

  function selectedPdpSellingPlanId(formId) {
    const form = formId ? document.getElementById(formId) : document.querySelector('form[action*="/cart/add"]');
    const input = form?.querySelector('input[name="selling_plan"]') || document.querySelector(`input[name="selling_plan"][form="${formId}"]`);
    const value = input?.value;
    return value ? Number(value) : null;
  }

  function selectedPdpVariantId(formId, fallback) {
    const form = formId ? document.getElementById(formId) : null;
    const input = form?.querySelector('[name="id"]');
    return input?.value ? Number(input.value) : fallback;
  }

  function snapshotLines(cart) {
    return (cart?.items || []).map((item) => ({
      key: item.key,
      id: item.variant_id || item.id,
      quantity: item.quantity,
      selling_plan: item.selling_plan_allocation?.selling_plan?.id || null,
    }));
  }

  async function restoreSnapshot(snapshot) {
    const live = await fetchCart();
    const originalByKey = Object.fromEntries((snapshot || []).map((line) => [line.key, line]));

    for (const item of live.items || []) {
      if (!originalByKey[item.key]) {
        await fetchJson(changeUrl(), {
          method: 'POST',
          body: JSON.stringify({ id: item.key, quantity: 0 }),
        }).catch(() => {});
      }
    }

    const afterAdds = await fetchCart();
    for (const item of afterAdds.items || []) {
      const original = originalByKey[item.key];
      if (!original) continue;
      const currentPlan = item.selling_plan_allocation?.selling_plan?.id || null;
      if (item.quantity === original.quantity && currentPlan === original.selling_plan) continue;
      const payload = {
        id: item.key,
        quantity: original.quantity,
        selling_plan: original.selling_plan,
      };
      await fetchJson(changeUrl(), {
        method: 'POST',
        body: JSON.stringify(payload),
      }).catch(() => {});
    }
  }

  async function addItems(items) {
    const response = await fetch(addUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ items }),
    });
    const json = await response.json();
    if (!response.ok || isShopifyErrorPayload(json)) {
      const description = typeof json.description === 'string' ? json.description : json.message;
      throw new Error(description || 'Could not add items to the cart.');
    }
    return json;
  }

  async function changeLine(item) {
    return fetchJson(changeUrl(), {
      method: 'POST',
      body: JSON.stringify(item),
    });
  }

  async function syncRebuyAndOpen() {
    const cartApi = window.Rebuy?.Cart;
    try {
      if (typeof cartApi?.fetch === 'function') {
        const fetched = cartApi.fetch();
        if (fetched?.then) await fetched;
      } else if (typeof cartApi?.getCart === 'function') {
        cartApi.getCart();
      } else if (typeof cartApi?.refresh === 'function') {
        cartApi.refresh();
      }
    } catch (error) {
      console.warn('[subscription-upsell] Rebuy cart sync failed', error);
    }

    document.dispatchEvent(new CustomEvent('rebuy:cart.add', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('rebuy:cart.refresh', { bubbles: true }));

    const smart = window.Rebuy?.SmartCart;
    if (smart) {
      if (typeof smart.show === 'function') smart.show();
      else if (typeof smart.open === 'function') smart.open();
      else if (typeof smart.expand === 'function') smart.expand();
    }
  }

  async function notifyCartChanged(cart) {
    if (FoxTheme?.pubsub?.publish && FoxTheme.pubsub.PUB_SUB_EVENTS?.cartUpdate) {
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
    }
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
    await syncRebuyAndOpen();
  }

  async function refreshDataSection() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}?section_id=subscription-upsell-data`);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('[data-subscription-upsell-data]');
      const current = queryInDocumentAndTemplates('[data-subscription-upsell-data]');
      if (next && current) current.replaceWith(next);
    } catch (error) {
      // keep stale map
    }
  }

  function currentVariantPlans(pairConfig) {
    return pairConfig.currentSellingPlans || [];
  }

  function liveSkioPlans() {
    const picker = document.querySelector('skio-plan-picker');
    const groups = picker?.availableSellingPlanGroups || [];
    return groups.flatMap((group) => group.selling_plans || []).map((plan) => ({ id: plan.id, name: plan.name }));
  }

  async function applyPair(pairConfig, source) {
    if (state.applying) throw new Error('Please wait for the previous add to finish.');
    state.applying = true;

    const cartBefore = await fetchCart();
    const snapshot = snapshotLines(cartBefore);
    const currentProductId = Number(pairConfig.currentProductId);
    const partnerProductId = Number(pairConfig.partner.productId);
    const formId = pairConfig.formId;
    const currentVariantId = selectedPdpVariantId(formId, pairConfig.currentVariantId);
    const partnerVariantId = Number(pairConfig.partner.variantId);

    const currentLine = findLineByVariant(cartBefore, currentVariantId);
    const partnerLine = findLineByVariant(cartBefore, partnerVariantId);

    const pdpPlanId = selectedPdpSellingPlanId(formId);
    const currentPlans = currentVariantPlans(pairConfig);
    const existingSub = [currentLine, partnerLine].find(isSubscription);
    const preferredName = existingSub?.selling_plan_allocation?.selling_plan?.name;
    const preferredId = existingSub?.selling_plan_allocation?.selling_plan?.id || pdpPlanId;

    const pickerPlans = liveSkioPlans();
    const currentPlan =
      pickSellingPlan(currentPlans, preferredName, preferredId) ||
      pickSellingPlan(pickerPlans, preferredName, preferredId);
    const partnerPlan = pickSellingPlan(pairConfig.partner.sellingPlans, currentPlan?.name || preferredName, preferredId);

    let mutated = false;
    try {
      if (!partnerPlan) {
        throw new Error('The selected product needs a subscription plan. Choose a Subscribe & Save variant in the metafield.');
      }

      const toAdd = [];
      if (!currentLine) {
        if (!currentPlan) throw new Error('Current selling plan unavailable');
        toAdd.push({ id: currentVariantId, quantity: 1, selling_plan: currentPlan.id });
      }
      if (!partnerLine) {
        toAdd.push({ id: partnerVariantId, quantity: 1, selling_plan: partnerPlan.id });
      }

      if (currentLine && !isSubscription(currentLine)) {
        const planId = currentPlan?.id || partnerPlan.id;
        await changeLine({ id: currentLine.key, quantity: currentLine.quantity, selling_plan: planId });
        mutated = true;
      }
      if (partnerLine && !isSubscription(partnerLine)) {
        await changeLine({ id: partnerLine.key, quantity: partnerLine.quantity, selling_plan: partnerPlan.id });
        mutated = true;
      }
      if (toAdd.length) {
        await addItems(toAdd);
        mutated = true;
      }

      if (!mutated) {
        throw new Error('Those products are already in your cart as subscriptions.');
      }

      const cartAfter = await fetchCart();
      await refreshDataSection();
      await notifyCartChanged(cartAfter);

      const eventPayload = {
        product_id: currentProductId,
        variant_id: currentLine ? currentLine.variant_id : currentVariantId,
        partner_product_id: partnerProductId,
        partner_variant_id: partnerLine ? partnerLine.variant_id : partnerVariantId,
      };

      if (source === 'pdp') pushEvent('subscription_upsell_add_success', eventPayload);
      else pushEvent('subscription_upsell_cart_conversion', eventPayload);
    } catch (error) {
      if (mutated) {
        try {
          await restoreSnapshot(snapshot);
        } catch (restoreError) {
          console.error('[subscription-upsell] restore failed', restoreError);
        }
      }
      throw error;
    } finally {
      state.applying = false;
      if (refresh.pending) scheduleRefresh();
    }
  }

  function setButtonLoading(button, loading) {
    if (!button) return;
    button.classList.toggle('is-loading', loading);
    button.toggleAttribute('disabled', loading);
    button.toggleAttribute('aria-busy', loading);
  }

  function showError(root, message) {
    root?.querySelectorAll('[data-subscription-upsell-error]').forEach((node) => {
      node.hidden = !message;
      node.textContent = message || '';
    });
  }

  function replaceDiscountCopy(root, percent) {
    const theme = mergePartnerMap();
    const strings = theme.strings || {};
    const config = parseJsonNode(root.querySelector('[data-subscription-upsell-config]'));
    const extras = {
      product: config?.partner?.label || config?.partner?.productTitle,
      variant: config?.partner?.variantTitle,
    };
    const saleNode = root.querySelector('[data-subscription-upsell-sale-price]');
    const regular = Number(config?.partner?.regularPrice);
    if (saleNode && Number.isFinite(regular) && regular > 0 && Number.isFinite(Number(percent))) {
      saleNode.textContent = formatUpsellMoney(Math.round(regular * (1 - Number(percent) / 100)));
    }
    if (config?.title) {
      root.querySelectorAll('[data-subscription-upsell-title]').forEach((node) => {
        node.textContent = formatCopy(config.title, percent, extras);
      });
    }
    const headingTemplate = config?.heading || strings.headingTemplate;
    if (headingTemplate) {
      root.querySelectorAll('[data-subscription-upsell-heading]').forEach((node) => {
        node.textContent = formatCopy(headingTemplate, percent, extras);
      });
    }
    if (config?.buttonLabel) {
      root.querySelectorAll('[data-subscription-upsell-cta]').forEach((node) => {
        node.textContent = formatCopy(config.buttonLabel, percent, extras);
      });
    }
    root.querySelectorAll('[data-subscription-upsell-banner-heading]').forEach((node) => {
      if (!strings.headingTemplate) return;
      node.textContent = formatCopy(strings.headingTemplate, percent, {
        ...extras,
        ...quantityCopyExtras(remainingToTier(state.cart)),
      });
    });
  }

  function renderTags(cart, classification, percent) {
    const theme = mergePartnerMap();
    const label = formatCopy(theme.strings?.tagLabelTemplate || '[discount] Subscribe & Save', percent);
    const pair = classification.pair;
    const show = classification.type === 'both_subs' && pair;
    const ids = show ? [Number(pair.currentProductId), Number(pair.partnerProductId)] : [];

    document.querySelectorAll('[data-subscription-upsell-tag]').forEach((node) => {
      const productId = Number(node.getAttribute('data-subscription-upsell-tag'));
      if (show && ids.includes(productId)) {
        node.hidden = false;
        node.textContent = label;
      } else {
        node.hidden = true;
        node.textContent = '';
      }
    });

  }

  function findRebuyMount() {
    return document.querySelector(`#rebuy-cart #${REBUY_MOUNT_ID}`);
  }

  function ensureRebuyMount() {
    const cart = document.getElementById('rebuy-cart');
    if (!cart) return null;
    const existing = findRebuyMount();
    if (existing) return existing;

    const mount = document.createElement('div');
    mount.id = REBUY_MOUNT_ID;
    const items = cart.querySelector('[data-rebuy-component="cart-items"], [data-smartcart-items]');
    if (items?.parentNode) items.parentNode.insertBefore(mount, items);
    else {
      const body = cart.querySelector('.rebuy-cart__flyout-body, .rebuy-cart__flyout-content') || cart;
      body.appendChild(mount);
    }
    return mount;
  }

  function removeStrayRebuyMounts() {
    const cart = document.getElementById('rebuy-cart');
    if (!cart) return;
    const inCart = findRebuyMount();
    if (!inCart) return;
    document.querySelectorAll(`#${REBUY_MOUNT_ID}`).forEach((node) => {
      if (!node.closest('#rebuy-cart')) node.remove();
    });
  }

  function cartNotificationsEnabled() {
    return getThemeConfig().cartNotificationsEnabled !== false;
  }

  function hideCartNotifications() {
    return withRebuyWrite(() => {
      document.querySelectorAll('[data-subscription-upsell-cart]').forEach((root) => {
        root.hidden = true;
      });
      const mount = findRebuyMount();
      if (mount) mount.innerHTML = '';
    });
  }

  function hydrateRebuyNotifications() {
    return withRebuyWrite(() => {
      if (!cartNotificationsEnabled()) {
        hideCartNotifications();
        return null;
      }
      const mount = ensureRebuyMount();
      if (!mount) return null;
      const root = mount.querySelector('[data-subscription-upsell-cart="rebuy"]');
      if (root) return root;
      mount.innerHTML = `
        <div class="subscription-upsell-cart" data-subscription-upsell-cart="rebuy">
          <p class="subscription-upsell-cart__savings" data-subscription-upsell-savings hidden role="status" aria-live="polite"></p>
          <p class="subscription-upsell-cart__banner" data-subscription-upsell-banner hidden role="status">
            <span data-subscription-upsell-banner-heading></span>
          </p>
        </div>
      `;
      return mount.querySelector('[data-subscription-upsell-cart="rebuy"]');
    });
  }

  function hideLossToasts() {
    clearTimeout(state.lossTimer);
    state.lossTimer = null;
    document.querySelectorAll('[data-subscription-upsell-savings]').forEach((node) => {
      node.hidden = true;
    });
  }

  function showLossToasts(percent, extra) {
    if (!cartNotificationsEnabled()) return;
    const theme = mergePartnerMap();
    const html = formatCopyHtml(theme.strings?.savingsLossTemplate || 'You lost [discount] off subscriptions', percent);
    document.querySelectorAll('[data-subscription-upsell-savings]').forEach((node) => {
      node.hidden = false;
      node.innerHTML = html;
    });
    pushEvent('subscription_upsell_savings_loss', extra);
    clearTimeout(state.lossTimer);
    state.lossTimer = setTimeout(() => {
      hideLossToasts();
      paintCartNotifications({ percent, atTier: false });
    }, LOSS_TOAST_MS);
  }

  function paintCartNotifications(discount) {
    if (!cartNotificationsEnabled()) {
      hideCartNotifications();
      return;
    }
    const subQty = subscriptionQty(state.cart);
    const needed = remainingToTier(state.cart);
    const showNudge = subQty >= 1 && needed > 0;
    const theme = mergePartnerMap();
    const nudgeHtml = formatCopyHtml(
      theme.strings?.headingTemplate || 'Add [quantity] more [subscription] to get [discount] off',
      discount.percent,
      quantityCopyExtras(needed)
    );

    document.querySelectorAll('[data-subscription-upsell-cart]').forEach((root) => {
      const banner = root.querySelector('[data-subscription-upsell-banner]');
      const heading = root.querySelector('[data-subscription-upsell-banner-heading]');
      const convert = root.querySelector('[data-subscription-upsell-convert]');
      const savings = root.querySelector('[data-subscription-upsell-savings]');
      if (heading) heading.innerHTML = nudgeHtml;
      if (banner) banner.hidden = !showNudge;
      if (convert) convert.hidden = true;
      const showLoss = savings && !savings.hidden;
      root.hidden = !showNudge && !showLoss;
    });
  }

  async function refreshCartUi() {
    hydrateRebuyNotifications();
    const cart = await fetchCart();
    const theme = mergePartnerMap();
    const classification = classify(cart, theme.products);
    const discount = await resolveDiscount(cart);
    const subQty = subscriptionQty(cart);
    const hadSnapshot = state.previousAtTier !== null;
    const droppedOffTier = hadSnapshot && state.previousAtTier && !discount.atTier;

    if (droppedOffTier) {
      showLossToasts(discount.percent, {
        product_id: classification.pair?.currentProductId,
        partner_product_id: classification.pair?.partnerProductId,
      });
    } else if (discount.atTier) {
      hideLossToasts();
    } else if (subQty < 1 && !state.lossTimer) {
      hideLossToasts();
    }

    state.previousAtTier = discount.atTier;

    paintCartNotifications(discount);
    renderTags(cart, classification, discount.percent);
  }

  function initPdpModule(root) {
    if (!root || root.dataset.ready) return;
    root.dataset.ready = 'true';
    const config = parseJsonNode(root.querySelector('[data-subscription-upsell-config]'));
    if (!config) return;

    const existing = state.pdpPairs.findIndex((pair) => Number(pair.currentProductId) === Number(config.currentProductId));
    if (existing > -1) state.pdpPairs[existing] = config;
    else state.pdpPairs.push(config);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!document.documentElement.contains(root)) {
            observer.disconnect();
            return;
          }
          if (!entry.isIntersecting || root.dataset.impressed) return;
          root.dataset.impressed = 'true';
          observer.disconnect();
          pushEvent('subscription_upsell_impression', {
            product_id: config.currentProductId,
            variant_id: selectedPdpVariantId(config.formId, config.currentVariantId),
            partner_product_id: config.partner.productId,
            partner_variant_id: config.partner.variantId,
          });
        });
      },
      { threshold: 0.4 }
    );
    observer.observe(root);

    root.querySelector('[data-subscription-upsell-cta]')?.addEventListener(
      'click',
      async (event) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      showError(root, '');
      const button = root.querySelector('[data-subscription-upsell-cta]');
      setButtonLoading(button, true);
      pushEvent('subscription_upsell_click', {
        product_id: config.currentProductId,
        variant_id: selectedPdpVariantId(config.formId, config.currentVariantId),
        partner_product_id: config.partner.productId,
        partner_variant_id: config.partner.variantId,
      });
      try {
        await applyPair(
          {
            ...config,
            currentVariantId: selectedPdpVariantId(config.formId, config.currentVariantId),
          },
          'pdp'
        );
      } catch (error) {
        console.error('[subscription-upsell] add failed', error);
        showError(root, error.message || getThemeConfig().strings?.error);
      } finally {
        setButtonLoading(button, false);
      }
    },
      true
    );

    resolveDiscount(state.cart).then(({ percent }) => replaceDiscountCopy(root, percent));
  }

  function bindVariantSync() {
    document.addEventListener('variant:changed', (event) => {
      const variant = event.detail?.variant;
      if (!variant?.id) return;
      state.pdpPairs.forEach((pair) => {
        pair.currentVariantId = variant.id;
      });
    });

    if (FoxTheme?.pubsub?.subscribe && FoxTheme.pubsub.PUB_SUB_EVENTS?.variantChange) {
      FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
        const variant = event.data?.variant;
        if (variant?.id) {
          state.pdpPairs.forEach((pair) => {
            pair.currentVariantId = variant.id;
          });
        }
      });
    }
  }

  function bindCartSync() {
    if (FoxTheme?.pubsub?.subscribe && FoxTheme.pubsub.PUB_SUB_EVENTS?.cartUpdate) {
      FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
        scheduleRefresh(event?.cart);
      });
    }

    ['rebuy:cart.add', 'rebuy:cart.change', 'rebuy:cart.remove', 'rebuy:cart.update', 'rebuy:cart.refresh'].forEach((name) => {
      document.addEventListener(name, () => scheduleRefresh());
    });

    const onSmartCartEvent = (event) => {
      attachRebuyObserver(document.getElementById('rebuy-cart'));
      hydrateRebuyNotifications();
      patchRebuySwitchLabel(event.detail?.smartcart);
      scheduleRefresh();
    };
    [
      'rebuy:smartcart.ready',
      'rebuy:smartcart.show',
      'rebuy:smartcart.line-item-removed',
      'rebuy:smartcart.line-item-increase',
      'rebuy:smartcart.line-item-decrease',
    ].forEach((name) => {
      document.addEventListener(name, onSmartCartEvent);
    });

    const attachRebuyObserver = (rebuyCart) => {
      if (!rebuyCart || rebuyCart.dataset.upsellObserved) return;
      rebuyCart.dataset.upsellObserved = 'true';
      let observerTimer;
      const observer = new MutationObserver(() => {
        if (hydratingRebuy) return;
        clearTimeout(observerTimer);
        observerTimer = setTimeout(() => {
          if (hydratingRebuy || state.applying) return;
          const mount = findRebuyMount();
          if (mount?.querySelector('[data-subscription-upsell-cart]')) return;
          hydrateRebuyNotifications();
        }, 150);
      });
      observer.observe(rebuyCart, { childList: true, subtree: true });
      rebuyCart.addEventListener('change', () => scheduleRefresh());
    };

    attachRebuyObserver(document.getElementById('rebuy-cart'));
  }

  function discoverPdpModules() {
    document.querySelectorAll('[data-subscription-upsell-pdp]').forEach(initPdpModule);
  }

  function bindPdpDiscovery() {
    discoverPdpModules();
    document.addEventListener('shopify:section:load', discoverPdpModules);
    const root = document.getElementById('MainContent') || document.body;
    const observer = new MutationObserver((mutations) => {
      const found = mutations.some((mutation) =>
        [...mutation.addedNodes].some(
          (node) =>
            node.nodeType === 1 &&
            (node.matches?.('[data-subscription-upsell-pdp]') || node.querySelector?.('[data-subscription-upsell-pdp]'))
        )
      );
      if (found) discoverPdpModules();
    });
    observer.observe(root, { childList: true, subtree: true });
  }

  function cachedSkioRules() {
    try {
      const raw = sessionStorage.getItem(SKIO_RULES_KEY);
      if (raw == null) return null;
      const rules = JSON.parse(raw);
      return Array.isArray(rules) ? rules : [];
    } catch (error) {
      return null;
    }
  }

  function sellingPlanPercent(item) {
    const fromProduct = Number(item?.product?.subscription_discount_amount);
    if (Number.isFinite(fromProduct) && fromProduct > 0) return fromProduct;
    const lineAdj = item?.selling_plan_allocation?.selling_plan?.price_adjustments?.[0];
    if (lineAdj && Number(lineAdj.value) > 0) return Number(lineAdj.value);
    const allocations =
      item?.product?.selected_variant?.selling_plan_allocations || item?.product?.selling_plan_allocations || [];
    const allocAdj = allocations[0]?.selling_plan?.price_adjustments?.[0];
    if (allocAdj && Number(allocAdj.value) > 0) return Number(allocAdj.value);
    return 21;
  }

  function lineCountsAsSubAfterConvert(line, convertingItem) {
    if (convertingItem && line?.key && line.key === convertingItem.key) return true;
    return isSubscription(line) || Boolean(line?.product?.subscription);
  }

  function eligibleQtyAfterSubscribe(rule, convertingItem, cartItems) {
    return (cartItems || []).reduce((sum, line) => {
      if (!line || !lineCountsAsSubAfterConvert(line, convertingItem)) return sum;
      if (!ruleMatchesItem(rule, line)) return sum;
      return sum + Number(line.quantity || 0);
    }, 0);
  }

  function pickVolumeRuleAfterSubscribe(convertingItem, cartItems, rules) {
    let best = null;
    (rules || []).forEach((rule) => {
      if (rule?.type === 'surpriseDelight') return;
      const min = Number(rule.minQuantityToDiscount);
      const amount = Number(rule.discountAmount);
      if (!Number.isFinite(min) || min < 1 || !Number.isFinite(amount) || amount <= 0) return;
      if (eligibleQtyAfterSubscribe(rule, convertingItem, cartItems) < min) return;
      if (!best || min > Number(best.minQuantityToDiscount)) best = rule;
    });
    return best;
  }

  function predictedSubscribePercent(item, cartItems) {
    const plan = sellingPlanPercent(item);
    const rule = pickVolumeRuleAfterSubscribe(item, cartItems, cachedSkioRules());
    const volume = Number(rule?.discountAmount);
    if (!Number.isFinite(volume) || volume <= 0) return plan;
    return stackedPercents(plan, volume);
  }

  function neuroRebuySwitchLabel(label, item, cartItems) {
    const percent = predictedSubscribePercent(item, cartItems);
    const text = String(label || '');
    if (/\d+(?:\.\d+)?%/.test(text)) return text.replace(/\d+(?:\.\d+)?%/, `${percent}%`);
    return `Subscribe & Save ${percent}%`;
  }

  function patchRebuySwitchLabel(smart) {
    try {
    const targets = [smart, window.Rebuy?.SmartCart];
    const el = document.querySelector('[data-rebuy-component="cart-items"]');
    if (el) targets.push(el.__vue__ || el.__vueParentComponent?.proxy);
    targets.forEach((target) => {
      if (!target) return;
      if (typeof target.switchToSubscriptionLabel === 'function' && !target.__neuroSwitchPatched) {
        const original = target.switchToSubscriptionLabel.bind(target);
        target.__neuroSwitchPatched = true;
        target.switchToSubscriptionLabel = function (item) {
          const cartItems = typeof this.items === 'function' ? this.items() : [];
          return neuroRebuySwitchLabel(original(item), item, cartItems);
        };
      }
      if (typeof target.itemPrice === 'function' && !target.__neuroPricePatched) {
        const originalPrice = target.itemPrice.bind(target);
        target.__neuroPricePatched = true;
        target.itemPrice = function (item) {
          const cartItems = typeof this.items === 'function' ? this.items() : [];
          const base = Number(originalPrice(item));
          if (!Number.isFinite(base)) return originalPrice(item);
          const isSub = Boolean(item?.product?.subscription || item?.selling_plan_allocation || item?.selling_plan_id);
          if (!isSub) return base;
          const rule = pickVolumeRuleAfterSubscribe(item, cartItems, cachedSkioRules());
          const volume = Number(rule?.discountAmount) || 0;
          return Math.round(base * (1 - volume / 100));
        };
      }
      if (typeof target.lineItemDiscountMessage === 'function' && !target.__neuroLineMsgPatched) {
        const originalLine = target.lineItemDiscountMessage.bind(target);
        target.__neuroLineMsgPatched = true;
        target.lineItemDiscountMessage = function (item) {
          const cartItems = typeof this.items === 'function' ? this.items() : [];
          if (!item?.product?.subscription && !item?.selling_plan_allocation) return originalLine(item);
          const plan = sellingPlanPercent(item);
          const percent = predictedSubscribePercent(item, cartItems);
          const boosted = percent > plan;
          const badgeClass = boosted
            ? 'rebuy-cart__discount-badge rebuy-cart__discount-badge--boosted'
            : 'rebuy-cart__discount-badge';
          return `<span class="${badgeClass}">${percent}% off</span>`;
        };
      }
    });
    } catch (error) {
      // Keep Rebuy usable if a patch target is mid-render.
    }
  }

  window.NeuroRebuySwitchLabel = neuroRebuySwitchLabel;

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  ready(() => {
    fetchSkioRules().catch(() => {});
    removeStrayRebuyMounts();
    bindCartSync();
    const hasPdp = Boolean(document.querySelector('[data-subscription-upsell-pdp]'));
    const hasCartUi = Boolean(document.querySelector('[data-subscription-upsell-cart], #rebuy-cart'));
    if (hasPdp) {
      bindPdpDiscovery();
      bindVariantSync();
    }
    if (hasPdp || hasCartUi) {
      hydrateRebuyNotifications();
      fetchCart()
        .then(() => refreshCartUi())
        .catch(() => {});
    }
  });
})();
